import { LoaderFunctionArgs } from '@remix-run/node';
import Layout from '~/components/layout';
import { validateUser } from '~/services/api/auth';
import { getCategoryList } from '~/services/api/categories';
import { Footer } from 'pdfs/components/footer';
import { typedjson, useTypedLoaderData } from 'remix-typedjson';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const limit = Number(url.searchParams.get('limit')) || 12;
  const page = Number(url.searchParams.get('page')) || undefined;
  const selectedCategory = url.searchParams.get('category');

  const { user } = await validateUser(request);
  const categories = await getCategoryList(limit, page);

  return typedjson({
    categories,
    user,
    selectedCategory,
  });
};

const FulfillmentPolicy = () => {
  const data = useTypedLoaderData<typeof loader>();

  return (
    <Layout user={data.user as unknown as NonNullable<Awaited<ReturnType<typeof validateUser>>['user']>}>
      <div className="container mx-auto mb-20 gap-4 px-4 sm:px-6 xl:px-56">
        <div className="flex flex-col items-center justify-center py-4">
          <h1 className="text-4xl">Fulfillment Policy</h1>
        </div>
        <div className="flex flex-col gap-2 pt-2">
          <p className="font-semibold">Effective Date: 10 October 2024</p>
          <p>
            This Fulfillment Policy (“Policy”) is issued by Best2bid.com (“Best2bid”, “we”, “our”, or “us”), a
            technology platform facilitating online auctions and bidding between independent sellers and buyers
            (“Users”). This Policy outlines the responsibilities and limitations of Best2bid with respect to the
            fulfillment of items listed and transacted through the platform.
          </p>
        </div>
        <div className="border-grey-100 mb-2 border-b py-4" />

        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">1. Platform Role and Limitation of Liability</p>
          <ul className="list-disc pl-6">
            <li>
              1.1 Best2bid is an online marketplace that enables third-party merchants, auction houses, and individual
              sellers (“Merchants”) to offer goods for sale or auction to buyers.
            </li>
            <li>
              1.2 Best2bid does not take possession of, own, or fulfill any goods listed on the platform. All
              obligations relating to the fulfillment, including but not limited to packaging, shipping, and delivery,
              rest solely with the Merchant responsible for the sale of the item.
            </li>
            <li>
              1.3 Best2bid shall not be held liable for any loss, delay, damage, or non-delivery of Items, whether
              caused by the Merchant, shipping providers, or other third parties.
            </li>
          </ul>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">2. Shipping and Delivery</p>
          <ul className="list-disc pl-6">
            <li>
              2.1 Merchants are independently responsible for:
              <ul className="list-disc pl-6">
                <li>Determining and displaying shipping methods, timelines, and applicable</li>
                <li>charges.</li>
                <li>Fulfilling successful transactions in accordance with their individual terms and</li>
                <li>within a reasonable time after auction or purchase completion.</li>
              </ul>
            </li>
            <li>
              2.2 Buyers are advised to carefully review the shipping and delivery details provided by the Merchant
              prior to bidding or purchase.
            </li>
            <li>
              2.3 Best2bid is not a party to any shipping agreement and shall not bear any responsibility for compliance
              with shipping deadlines or conditions.
            </li>
          </ul>
        </div>

        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">3. Local Pickup (if applicable)</p>
          <ul className="list-disc pl-6">
            <li>
              3.1 Certain items may be designated by the Merchant as available for local pickup. Details regarding
              pickup times, locations, and procedures shall be communicated directly by the Merchant.
            </li>
            <li>
              3.2 Best2bid shall not be responsible for coordinating or supervising any local pickup arrangements.
            </li>
          </ul>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">4. Dispute Resolution and Communication Support</p>
          <ul className="list-disc pl-6">
            <li>
              4.1 In the event of any dispute or issue concerning fulfillment, including but not limited to non-receipt,
              damaged goods, or late delivery, the affected User shall contact the Merchant directly using the contact
              information provided on the invoice or item page.
            </li>
            <li>
              4.2 While Best2bid is not responsible for fulfillment-related disputes, we may, at our discretion,
              facilitate communication between the parties in an attempt to resolve the issue amicably.
            </li>
            li4.3 Best2bid does not guarantee a resolution and shall not provide refunds, reimbursements, or
            compensation for fulfillment issues. For refund-related concerns, please refer to our [Refund Policy].
          </ul>
        </div>
        <div className="mt-10 flex flex-col gap-4">
          <p className="text-2xl">5. Changes to This Policy</p>
          <ul className="list-disc pl-6">
            <li>
              Best2bid reserves the right to amend or update this Fulfillment Policy at any time. Users are encouraged
              to review this Policy periodically to stay informed of any changes.
            </li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default FulfillmentPolicy;
